import { TABTestWithStats, TABTestStatus, TWinnerPath, TABTestsCache, TABTestStats } from "./types"

/**
 * Determines the status of an A/B test based on its data
 *
 *
 * Draft: No committed_revision
 *
 * Active vs Completed Logic (lines 1767-1882 in original):
 * 1. First checks pathC stats:
 *    - If pathC has sent > 0 → COMPLETED (winner sent via pathC)
 *    - If pathC has sent === 0 or null, check pathD
 *
 * 2. Then checks pathD stats:
 *    - If pathD has sent > 0 → COMPLETED (winner sent via pathD)
 *    - If pathD has sent === 0 or null, check automation status
 *
 * 3. If NO winner stats (both pathC and pathD are 0 or null):
 *    - If plan.status === "active" → ACTIVE (test running, no winner yet)
 *    - If plan.status !== "active" → COMPLETED (test stopped/cancelled without winner)
 *
 * Edge cases:
 * - Stats API can fail → treat as null/0
 * - Stats can return empty object {} → treat as null/0
 * - Both statsC and statsD can be null → check automation.status
 *
 * @param abTest - The A/B test automation object
 * @returns The calculated status
 */
export function getABTestStatus(abTest: TABTestWithStats): TABTestStatus {
    // DRAFT: No committed_revision (hasn't been published/started)
    if (!abTest.committed_revision) {
        return "draft"
    }

    // Check pathC stats first (primary winner path)
    const hasWinnerStatsC = abTest.statsC?.data?.groupedByContact?.sent && abTest.statsC.data.groupedByContact.sent > 0

    if (hasWinnerStatsC) {
        return "completed"
    }

    // Check pathD stats (alternative winner path)
    const hasWinnerStatsD = abTest.statsD?.data?.groupedByContact?.sent && abTest.statsD.data.groupedByContact.sent > 0

    if (hasWinnerStatsD) {
        return "completed"
    }

    // NO WINNER SENT: Check automation status
    // If automation.status === "active", it's still running the A/B test (active)
    // Otherwise, test was stopped/cancelled without sending winner (completed but no winner)
    if (abTest.status === "active") {
        return "active"
    }

    // Test stopped/cancelled/paused without winner being sent
    return "completed"
}

/**
 * Filters A/B tests by the specified status
 *
 * @param abTests - Array of A/B tests with stats
 * @param status - Status to filter by ("draft" | "active" | "completed" | "all")
 * @returns Filtered array of A/B tests
 */
export function filterABTestsByStatus(
    abTests: TABTestWithStats[],
    status: TABTestStatus | "all"
): TABTestWithStats[] {
    if (status === "all") {
        return abTests
    }

    return abTests.filter(test => getABTestStatus(test) === status)
}

/**
 * Determines which path won the A/B test
 *
 * Priority:
 * 1. If pathC has sent emails, winner is pathC
 * 2. If pathD has sent emails, winner is pathD
 * 3. Otherwise, fallback to pathA (default for drafts/active tests)
 *
 * @param abTest - The A/B test with stats
 * @returns The winning path identifier
 */
export function getWinnerPath(abTest: TABTestWithStats): TWinnerPath {
    // Check pathC first
    if (abTest.statsC?.data?.groupedByContact?.sent && abTest.statsC.data.groupedByContact.sent > 0) {
        return "pathC"
    }

    // Check pathD
    if (abTest.statsD?.data?.groupedByContact?.sent && abTest.statsD.data.groupedByContact.sent > 0) {
        return "pathD"
    }

    // Default to pathA (used for drafts and active tests)
    return "pathA"
}

/**
 * Aggregates statistics from two paths
 * Used to combine winner stats (pathC + pathA or pathD + pathB) for total metrics
 *
 * @param statsA - First stats object
 * @param statsB - Second stats object
 * @returns Combined stats object with summed metrics
 */
export function aggregateStats(statsA?: TABTestStats, statsB?: TABTestStats): TABTestStats | undefined {
    // If neither stats exist, return undefined
    if (!statsA && !statsB) {
        return undefined
    }

    // If only one exists, return it
    if (!statsA) return statsB
    if (!statsB) return statsA

    // Sum all metrics from both paths
    return {
        data: {
            groupedByContact: {
                open: statsA.data.groupedByContact.open + statsB.data.groupedByContact.open,
                clickthru: statsA.data.groupedByContact.clickthru + statsB.data.groupedByContact.clickthru,
                sent: statsA.data.groupedByContact.sent + statsB.data.groupedByContact.sent,
                subscribe: statsA.data.groupedByContact.subscribe + statsB.data.groupedByContact.subscribe,
                unsubscribe: statsA.data.groupedByContact.unsubscribe + statsB.data.groupedByContact.unsubscribe,
                bounce: statsA.data.groupedByContact.bounce + statsB.data.groupedByContact.bounce,
                soft_bounce: statsA.data.groupedByContact.soft_bounce + statsB.data.groupedByContact.soft_bounce,
                hard_bounce: statsA.data.groupedByContact.hard_bounce + statsB.data.groupedByContact.hard_bounce,
                spam: statsA.data.groupedByContact.spam + statsB.data.groupedByContact.spam
            },
            totals: {
                open: statsA.data.totals.open + statsB.data.totals.open,
                clickthru: statsA.data.totals.clickthru + statsB.data.totals.clickthru,
                sent: statsA.data.totals.sent + statsB.data.totals.sent,
                subscribe: statsA.data.totals.subscribe + statsB.data.totals.subscribe,
                unsubscribe: statsA.data.totals.unsubscribe + statsB.data.totals.unsubscribe,
                bounce: statsA.data.totals.bounce + statsB.data.totals.bounce,
                soft_bounce: statsA.data.totals.soft_bounce + statsB.data.totals.soft_bounce,
                hard_bounce: statsA.data.totals.hard_bounce + statsB.data.totals.hard_bounce,
                spam: statsA.data.totals.spam + statsB.data.totals.spam
            }
        }
    }
}

export function calculateClickRate(stats?: TABTestStats): number {
    if (!stats || !stats.data.groupedByContact.sent || stats.data.groupedByContact.sent === 0) {
        return 0
    }
    return (stats.data.groupedByContact.clickthru / stats.data.groupedByContact.sent) * 100
}

export function calculateOpenRate(stats?: TABTestStats): number {
    if (!stats || !stats.data.groupedByContact.sent || stats.data.groupedByContact.sent === 0) {
        return 0
    }
    return (stats.data.groupedByContact.open / stats.data.groupedByContact.sent) * 100
}

export function calculateUnsubscribeRate(stats?: TABTestStats): number {
    if (!stats || !stats.data.groupedByContact.sent || stats.data.groupedByContact.sent === 0) {
        return 0
    }
    return (stats.data.groupedByContact.unsubscribe / stats.data.groupedByContact.sent) * 100
}

export function calculateBounceRate(stats?: TABTestStats): number {
    if (!stats || !stats.data.groupedByContact.sent || stats.data.groupedByContact.sent === 0) {
        return 0
    }
    return (stats.data.groupedByContact.bounce / stats.data.groupedByContact.sent) * 100
}


const AB_TESTS_CACHE_KEY = "abTestsCache"

export function getABTestsCache(): TABTestsCache {
    try {
        const cached = localStorage.getItem(AB_TESTS_CACHE_KEY)
        if (cached) {
            return JSON.parse(cached)
        }
    } catch (e) {

    }
    return {}
}

export function setABTestsCache(cache: TABTestsCache): void {
    try {
        localStorage.setItem(AB_TESTS_CACHE_KEY, JSON.stringify(cache))
    } catch (e) {
        console.error("Error writing A/B tests cache:", e)
    }
}

export function hasWinner(abTest: TABTestWithStats): boolean {
    return getABTestStatus(abTest) === "completed"
}

export function extractContentIds(abTest: TABTestWithStats): {
    pathA?: string
    pathB?: string
    pathC?: string
    pathD?: string
} {
    const contentIds: Record<string, string> = {}
    // A/B test emails are stored in sub_automations, not main.steps
    if (abTest.sub_automations && Array.isArray(abTest.sub_automations)) {
        abTest.sub_automations.forEach((subAutomation: any) => {
            const subName = subAutomation.name

            if (subName && (subName === "pathA" || subName === "pathB" || subName === "pathC" || subName === "pathD")) {

                if (subAutomation.steps && subAutomation.steps.length > 0) {
                    const firstStep = subAutomation.steps[0]

                    if (firstStep.action === "CampaignEmail.Send" && firstStep.args?.content?.id) {
                        if (firstStep.args.content.id.trim() !== "") {
                            contentIds[subName] = firstStep.args.content.id
                        }
                    }
                }
            }
        })
    }

    return {
        pathA: contentIds.pathA,
        pathB: contentIds.pathB,
        pathC: contentIds.pathC,
        pathD: contentIds.pathD
    }
}
